import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { ActionButton, TabItem } from '@components/LabsAppBar';
import LabsAppBar from '@components/LabsAppBar';
import { useNotifications } from '@contexts/NotificationContext';
import PlaybookDetailsTab from '@components/playbooks/PlaybookDetailsTab';
import PlaybookEditorTab from '@components/playbooks/PlaybookEditorTab';
import ConnectionEditorTab from '@components/playbooks/ConnectionEditorTab';
import ConfirmDialog from '@components/dialogs/ConfirmDialog';
import PersistenceConflictDialog from '@components/dialogs/PersistenceConflictDialog';
import { PlaybookTransformItem, PlaybookEventConfig, Playbook, PlaybookParamValue, ConnectionConfig, ConnectionParam } from '@/types/playbook';
import { exportPlaybookAndConnectionYaml } from '@utils/playbookUtils';
import { processImportFile, applyImportedPlaybookData, applyImportedConnectionData, ImportFileResult } from '@utils/playbookImportUtils';
import ImportOverlay from '@components/playbooks/ImportOverlay';
import ImportResultsDialog from '@components/playbooks/ImportResultsDialog';
import isEqual from 'lodash-es/isEqual';
import { usePlaybookEditor } from '@hooks/usePlaybookEditor';

// Define ValidationWarning type for compatibility
interface ValidationWarning {
  message: string;
  type: 'warning' | 'error';
}

// Legacy PlaybookEditState interface for compatibility with existing components
export interface PlaybookEditState {
  playbookId: string;
  name: string;
  description: string;
  type: Playbook['type'];
  version: string;
  params: PlaybookParamValue[];
  input_schema_id?: string;
  output_schema_id?: string;
  uiTransforms: PlaybookTransformItem[];
  uiEventConfigs: PlaybookEventConfig[];
  autoPromoteGlobals: boolean;
  allowUndefinedTransforms: boolean;
  isNewPlaybook: boolean;
  isSubmitting: boolean;
  baselineSchema?: 'input' | 'output';
  __originalYaml?: any;
  id: string;
  content: any;
  status: 'saved';
  connectionJSON: ConnectionConfig | null;
  activeTab?: string;
}

// Simple validation function for legacy compatibility
const validatePlaybookState = (state: any): ValidationWarning[] => {
  const warnings: ValidationWarning[] = [];
  
  if (!state.name?.trim()) {
    warnings.push({ message: 'Playbook name is required', type: 'error' });
  }
  
  if (!state.input_schema_id) {
    warnings.push({ message: 'Input schema is required', type: 'warning' });
  }
  
  if (!state.output_schema_id) {
    warnings.push({ message: 'Output schema is required', type: 'warning' });
  }
  
  return warnings;
};

const PlaybookEditPage: React.FC = () => {
  // Use the new unified playbook editor hook
  const editor = usePlaybookEditor();
  
  // Extract commonly used values from the editor
  const {
    playbookState,
    updatePlaybookState,
    isLoading,
    hasUnsavedChanges,
    validationResult,
    persistenceConflict,
    saveState,
    resolveConflict,
    playbookId,
    tab,
    playbookType,
    navigateToList,
    currentSchemaView,
    setCurrentSchemaView,
    currentYamlView,
    setCurrentYamlView,
    selection,
    setSelection,
    labsUIState,
    updateLabsUIState,
  } = editor;

  const { showNotification } = useNotifications();
  
  // Convert validation result to legacy format for compatibility
  const validationWarnings: ValidationWarning[] = useMemo(() => {
    return validationResult.warnings.map(warning => ({
      message: warning,
      type: 'warning' as const
    }));
  }, [validationResult.warnings]);

  // Local state for dialogs and import functionality
  const [showConfirmCancelDialog, setShowConfirmCancelDialog] = useState(false);
  const [showImportOverlay, setShowImportOverlay] = useState(false);
  const [showImportResults, setShowImportResults] = useState(false);
  const [importResults, setImportResults] = useState<ImportFileResult[]>([]);
  const [isDragOverGlobal, setIsDragOverGlobal] = useState(false);
  const dragCounterGlobal = useRef(0);
  const dragLeaveTimeoutRef = useRef<number | null>(null);

  // Load playbook data on mount
  useEffect(() => {
    if (playbookId && playbookId !== 'new') {
      editor.loadState(playbookId);
    }
  }, [playbookId]); // Remove editor.loadState from dependencies to prevent infinite loops

  // Cleanup on unmount - clear storage for unsaved changes
  useEffect(() => {
    return () => {
      // Only cleanup if we have unsaved changes to prevent clearing saved state
      if (hasUnsavedChanges) {
        editor.cleanup();
      }
    };
  }, [hasUnsavedChanges, editor.cleanup]);

  // Update active tab when URL changes
  useEffect(() => {
    const currentTab = tab || 'details';
    if (playbookState.activeTab !== currentTab) {
      updatePlaybookState({ activeTab: currentTab });
    }
  }, [tab, playbookState.activeTab, updatePlaybookState]);

  // Handle connection params sync
  useEffect(() => {
    if (playbookState.connectionJSON && playbookState.connectionJSON.params) {
      const newParams: PlaybookParamValue[] = playbookState.connectionJSON.params.map((paramDef: ConnectionParam) => {
        const existingParam = playbookState.params.find((p: PlaybookParamValue) => p.name === paramDef.name);
        return {
          name: paramDef.name,
          value: existingParam?.value ?? paramDef.defaultValue ?? '',
          isPipelineVariable: existingParam?.isPipelineVariable ?? paramDef.isPipelineVariable ?? false,
        };
      });

      if (!isEqual(newParams, playbookState.params)) {
        updatePlaybookState({ params: newParams });
      }
    }
  }, [playbookState.connectionJSON?.params, playbookState.params, updatePlaybookState]);

  // Handle save changes
  const handleSaveChanges = useCallback(async () => {
    const success = await saveState();
    if (success && playbookState.isNewPlaybook) {
      // Navigate to the new playbook after creation
      editor.navigateToEdit(playbookState.id, 'details');
    }
  }, [saveState, playbookState.isNewPlaybook, playbookState.id, editor.navigateToEdit]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowConfirmCancelDialog(true);
    } else {
      navigateToList();
    }
  }, [hasUnsavedChanges, navigateToList]);

  const confirmCancel = useCallback(() => {
    editor.cleanup();
    navigateToList();
  }, [editor.cleanup, navigateToList]);

  // Handle export
  const handleExport = useCallback(async (action: 'playbook-yaml' | 'connection-yaml' | 'combined-gz' | 'combined-yaml') => {
    const warnings = validatePlaybookState(playbookState);
    if (warnings.length > 0) {
      showNotification('warning', `Exporting with warnings: ${warnings.map(w => w.message).join(', ')}`);
    }

    try {
      // Use the unified export function to get clean YAML
      const { playbookYaml, connectionYaml } = exportPlaybookAndConnectionYaml(playbookState as any);

      switch (action) {
        case 'playbook-yaml': {
          const blob = new Blob([playbookYaml], { type: 'application/x-yaml' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'playbook.yaml';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          showNotification('success', 'Playbook YAML downloaded successfully.');
          break;
        }
        case 'connection-yaml': {
          if (!playbookState.connectionJSON) {
            showNotification('error', 'Connection data is not available for export.');
            return;
          }
          const blob = new Blob([connectionYaml], { type: 'application/x-yaml' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'connection.yaml';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          showNotification('success', 'Connection YAML downloaded successfully.');
          break;
        }
        case 'combined-gz': {
          if (!playbookState.connectionJSON) {
            showNotification('error', 'Connection data is not available for export for v1.0 compatible GZ.');
            return;
          }
          // For now, just download as combined YAML since GZ compression is not implemented
          const combinedYaml = `### Connection Configuration\n${connectionYaml}\n---\n### Playbook Definition\n${playbookYaml}`;
          const blob = new Blob([combinedYaml], { type: 'application/x-yaml' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'combined.yaml';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          showNotification('success', 'Combined YAML downloaded successfully.');
          break;
        }
        case 'combined-yaml': {
          if (!playbookState.connectionJSON) {
            showNotification('error', 'Connection data is not available for export.');
            return;
          }
          const combinedYaml = `### Connection Configuration\n${connectionYaml}\n---\n### Playbook Definition\n${playbookYaml}`;
          const blob = new Blob([combinedYaml], { type: 'application/x-yaml' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'combined.yaml';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          showNotification('success', 'Combined Playbook YAML downloaded successfully.');
          break;
        }
        default:
          showNotification('error', 'Unknown export action.');
      }
    } catch (error: any) {
      showNotification('error', `Failed to export playbook: ${error.message}.`);
    }
  }, [playbookState, showNotification]);

  // Breadcrumbs
  const breadcrumbs = useMemo(() => {
    const basePlaybooksPath = playbookState.type === 'integration' ? '/labs/integrations' : '/labs/playbooks';
    const basePlaybooksLabel = playbookState.type === 'integration' ? 'Playbook Editor' : 'Pipeline Transforms';
    const basePlaybooksIcon = playbookState.type === 'integration' ? 'Destinations' : 'Pipelines';

    return [
      { name: basePlaybooksLabel, iconName: basePlaybooksIcon, path: basePlaybooksPath },
      { name: playbookState.name || 'New Playbook' },
    ];
  }, [playbookState.name, playbookState.type]);

  // Tabs
  const tabs: TabItem[] = useMemo(() => {
    const idForPath = playbookId || 'new';
    const basePath = playbookType === 'pipeline' ? '/labs/playbooks' : '/labs/integrations';
    
    const baseTabs: TabItem[] = [
      { label: 'Playbook Details', path: `${basePath}/${idForPath}/edit/details`, iconName: 'Pencil' },
      { label: 'Playbook Editor', path: `${basePath}/${idForPath}/edit/editor` },
    ];

    // Conditionally add Connection Settings tab for 'integration' type playbooks
    if (playbookState.type === 'integration') {
      baseTabs.push({ label: 'Connection Settings', path: `${basePath}/${idForPath}/edit/connection` });
    }

    return baseTabs;
  }, [playbookId, playbookType, playbookState.type]);

  // Actions
  const actions: ActionButton[] = useMemo(() => {
    const saveButtonLabel = playbookState.isNewPlaybook ? 'Save Playbook' : 'Save Changes';
    const saveButtonLoading = playbookState.isSubmitting;
    
    // For new playbooks, allow saving even without unsaved changes
    // For existing playbooks, only allow saving when there are unsaved changes
    const canSave = playbookState.isNewPlaybook || hasUnsavedChanges;

    const commonActions: ActionButton[] = [
      { label: 'Back', onClick: handleCancel, variant: 'text' },
      {
        label: saveButtonLoading ? 'Saving...' : saveButtonLabel,
        onClick: handleSaveChanges,
        variant: 'secondary',
        loading: saveButtonLoading,
        disabled: saveButtonLoading || !canSave,
      },
    ];

    if (!playbookState.isNewPlaybook) {
      commonActions.push({
        label: 'Export',
        onClick: () => handleExport('playbook-yaml'),
        variant: 'primary',
        disabled: false,
      });
    }

    return commonActions;
  }, [
    handleCancel,
    handleSaveChanges,
    handleExport,
    playbookState.isSubmitting,
    playbookState.isNewPlaybook,
    hasUnsavedChanges,
  ]);

  // Active tab path
  const activeTabPath = useMemo(() => {
    const currentTabParam = tab || 'details';
    const idForPath = playbookId || 'new';
    const basePath = playbookType === 'pipeline' ? '/labs/playbooks' : '/labs/integrations';

    switch (currentTabParam) {
      case 'details':
        return `${basePath}/${idForPath}/edit/details`;
      case 'editor':
        return `${basePath}/${idForPath}/edit/editor`;
      case 'connection':
        return `${basePath}/${idForPath}/edit/connection`;
      default:
        return `${basePath}/${idForPath}/edit/details`;
    }
  }, [tab, playbookId, playbookType]);

  // Import functionality handlers
  const handleDragEnterGlobal = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (dragLeaveTimeoutRef.current) {
      clearTimeout(dragLeaveTimeoutRef.current);
      dragLeaveTimeoutRef.current = null;
    }

    dragCounterGlobal.current += 1;
    const hasFiles = e.dataTransfer?.types?.includes('Files');
    if (hasFiles && dragCounterGlobal.current === 1) {
      setIsDragOverGlobal(true);
      setShowImportOverlay(true);
    }
  }, []);

  const handleDragLeaveGlobal = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterGlobal.current -= 1;

    if (dragCounterGlobal.current === 0) {
      if (dragLeaveTimeoutRef.current) {
        clearTimeout(dragLeaveTimeoutRef.current);
      }
      dragLeaveTimeoutRef.current = window.setTimeout(() => {
        setIsDragOverGlobal(false);
        setShowImportOverlay(false);
        dragLeaveTimeoutRef.current = null;
      }, 50);
    }
  }, []);

  const handleDragOverGlobal = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleImportFiles = useCallback(async (results: ImportFileResult[]) => {
    setImportResults(results);
    setShowImportOverlay(false);
    setShowImportResults(true);
  }, []);

  const handleDropGlobal = useCallback(async (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOverGlobal(false);
    dragCounterGlobal.current = 0;
    
    const files = Array.from(e.dataTransfer?.files || []);
    
    if (files.length > 0) {
      try {
        const results: ImportFileResult[] = [];
        
        for (const file of files) {
          const result = await processImportFile(file, playbookState as any);
          results.push(result);
        }
        handleImportFiles(results);
      } catch (error) {
        console.error('Error processing import files in global handler:', error);
      }
    }
  }, [playbookState, handleImportFiles]);

  const handleImportConfirm = useCallback(async (selectedResults: ImportFileResult[]) => {
    let updatedState = { ...playbookState };
    try {
      let hasPlaybookImport = false;
      let hasConnectionImport = false;

      for (const result of selectedResults) {
        if (result.fileType === 'playbook') {
          const playbookUpdates = applyImportedPlaybookData(updatedState as any, result.data);
          updatedState = { ...updatedState, ...playbookUpdates };
          hasPlaybookImport = true;
        } else if (result.fileType === 'connection') {
          const connectionUpdates = applyImportedConnectionData(result.data);
          updatedState = { ...updatedState, ...connectionUpdates };
          hasConnectionImport = true;
        }
      }

      updatePlaybookState(updatedState);

      const importedTypes = [];
      if (hasPlaybookImport) importedTypes.push('playbook');
      if (hasConnectionImport) importedTypes.push('connection');
      
      showNotification('success', `Successfully imported ${importedTypes.join(' and ')} data.`);
      
      const warnings = selectedResults
        .filter(r => r.validationResult.warningMessage)
        .map(r => r.validationResult.warningMessage);
      
      if (warnings.length > 0) {
        showNotification('warning', `Import completed with warnings: ${warnings.join(', ')}.`);
      }

    } catch (error) {
      console.error('Failed to import files:', error);
      showNotification('error', `Failed to import files: ${error instanceof Error ? error.message : 'Unknown error'}.`);
    } finally {
      setShowImportResults(false);
      setImportResults([]);
    }
  }, [playbookState, updatePlaybookState, showNotification]);

  const handleImportCancel = useCallback(() => {
    setShowImportResults(false);
    setImportResults([]);
  }, []);

  const handleImportOverlayClose = useCallback(() => {
    setShowImportOverlay(false);
    setIsDragOverGlobal(false);
    dragCounterGlobal.current = 0;
  }, []);

  // Set up global drag and drop listeners
  useEffect(() => {
    const handleGlobalDragEnter = (e: DragEvent) => handleDragEnterGlobal(e);
    const handleGlobalDragLeave = (e: DragEvent) => handleDragLeaveGlobal(e);
    const handleGlobalDragOver = (e: DragEvent) => handleDragOverGlobal(e);
    const handleGlobalDrop = (e: DragEvent) => handleDropGlobal(e);

    document.addEventListener('dragenter', handleGlobalDragEnter);
    document.addEventListener('dragleave', handleGlobalDragLeave);
    document.addEventListener('dragover', handleGlobalDragOver);
    document.addEventListener('drop', handleGlobalDrop);

    return () => {
      document.removeEventListener('dragenter', handleGlobalDragEnter);
      document.removeEventListener('dragleave', handleGlobalDragLeave);
      document.removeEventListener('dragover', handleGlobalDragOver);
      document.removeEventListener('drop', handleGlobalDrop);
    };
  }, [handleDragEnterGlobal, handleDragLeaveGlobal, handleDragOverGlobal, handleDropGlobal]);

  return (
    <div className="flex flex-col h-screen">
      <LabsAppBar
        breadcrumbs={breadcrumbs}
        actions={actions}
        tabs={tabs}
        selectedTabPath={activeTabPath}
        onExport={!playbookState.isNewPlaybook && playbookState.type === 'integration' && playbookState.connectionJSON ? handleExport : undefined}
      />
      <div className="flex-grow bg-gray-50 dark:bg-gray-900 overflow-hidden relative">
        {isLoading ? (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-100/75 dark:bg-gray-900/75 backdrop-blur-sm z-100">
            <div className="flex flex-col items-center p-8 rounded-lg shadow-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
              <div className="text-gray-600 dark:text-gray-400 text-xl font-semibold whitespace-nowrap">Loading Playbook...</div>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-2 whitespace-nowrap">Please wait while we fetch the data.</p>
            </div>
          </div>
        ) : (
          <>
            {(tab || 'details') === 'details' && (
              <PlaybookDetailsTab state={playbookState as any} updateState={updatePlaybookState} />
            )}
            {(tab || 'details') === 'editor' && (
              <>
                {playbookState.input_schema_id && playbookState.output_schema_id ? (
                  <PlaybookEditorTab
                    state={playbookState as any}
                    updateState={updatePlaybookState}
                    validationWarnings={validationWarnings}
                    currentSchemaView={currentSchemaView}
                    setCurrentSchemaView={setCurrentSchemaView}
                    currentYamlView={currentYamlView}
                    setCurrentYamlView={setCurrentYamlView}
                    selection={selection}
                    setSelection={setSelection}
                    searchTerm={labsUIState.searchTerm}
                    setSearchTerm={(term: string) => updateLabsUIState({ searchTerm: term })}
                    viewMode={labsUIState.viewMode}
                    setViewMode={(mode: any) => updateLabsUIState({ viewMode: mode })}
                    rightPanelWidth={labsUIState.rightPanelWidth}
                    setRightPanelWidth={((widthOrUpdater: React.SetStateAction<number>) => {
                      if (typeof widthOrUpdater === 'function') {
                        const newWidth = widthOrUpdater(labsUIState.rightPanelWidth);
                        updateLabsUIState({ rightPanelWidth: newWidth });
                      } else {
                        updateLabsUIState({ rightPanelWidth: widthOrUpdater });
                      }
                    })}
                    isRightPanelCollapsed={labsUIState.isRightPanelCollapsed}
                    setIsRightPanelCollapsed={(collapsed: boolean) => updateLabsUIState({ isRightPanelCollapsed: collapsed })}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center p-8 bg-white dark:bg-gray-800 rounded-lg shadow-md">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Missing Schema Information
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Please select input and output schemas in the Playbook Details tab before using the editor.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
            {(tab || 'details') === 'connection' && playbookState.type === 'integration' && (
              <ConnectionEditorTab 
                connectionJSON={playbookState.connectionJSON} 
                onUpdateConnection={(connection) => updatePlaybookState({ connectionJSON: connection })} 
              />
            )}
          </>
        )}
      </div>

      {/* Persistence Conflict Dialog */}
      {persistenceConflict && (
        <PersistenceConflictDialog
          isOpen={true}
          onLoadBackend={() => resolveConflict(true)}
          onLoadPersisted={() => resolveConflict(false)}
          onClose={() => {
            // Default to loading backend state when closing via X or escape
            resolveConflict(true);
          }}
          editorType="playbook"
          itemName={playbookState.name || 'Untitled Playbook'}
          persistedTimestamp={persistenceConflict.timestamp}
          backendTimestamp={persistenceConflict.backendState.lastSaved}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      {showConfirmCancelDialog && (
        <ConfirmDialog
          onClose={() => setShowConfirmCancelDialog(false)}
          title="Discard Unsaved Changes?"
          message="You have unsaved changes. Are you sure you want to discard them and cancel?"
          onConfirm={confirmCancel}
        />
      )}

      {/* Import Overlay */}
      <ImportOverlay
        isVisible={showImportOverlay}
        isDragActive={isDragOverGlobal}
        onImportFiles={handleImportFiles}
        onClose={handleImportOverlayClose}
        playbookState={playbookState as any}
      />

      {/* Import Results Dialog */}
      <ImportResultsDialog
        isOpen={showImportResults}
        results={importResults}
        onConfirm={handleImportConfirm}
        onCancel={handleImportCancel}
      />
    </div>
  );
};

export default PlaybookEditPage;
